import { Dispatch } from 'react'

import { consoleEvents, getLabelFromStorage, getLocale } from '@pb/utils'
import { mergeRight } from 'ramda'

import optimizelyConstants from 'constants/optimizely'
import { ACCOUNT_TRACKING_EVENTS } from 'features/account/constants'
import donateVoucherActions from 'features/account/donate-voucher/duck/actions'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import { trackEventAdapter } from 'helpers/tracking'

import { MENU_ITEM_KEYS, MENU_SECTION_KEYS, NEW_MENU_ITEM_KEYS, NEW_MENU_KEYS } from '../constants'
import { MenuItem, MenuToggles, UserMenu } from '../duck/types'

const sortByRank = (itemA, itemB): any => {
  if (itemA.rank < itemB.rank) return -1
  if (!itemB.rank && itemA.rank) return -1
  return 0
}

// Merge the default menu configuration set by React, with the menu config override
// from Optimizely Fullstack and then sort menus and menuItems by rank.
const mergeDefaultMenuWithOFS = (defaultMenus: UserMenu[], ofsMenus: UserMenu[]): UserMenu[] => {
  try {
    return defaultMenus
      .map((menu) => {
        const matchingOFSMenu = ofsMenus?.find((ofsMenu) => ofsMenu.key === menu.key)
        const items = menu.menuItems.map((item) => {
          const matchingOFSMenuItem = matchingOFSMenu?.menuItems?.find(
            (menuItem) => menuItem.key === item.key,
          )
          if (matchingOFSMenu && matchingOFSMenuItem) {
            return mergeRight(item, matchingOFSMenuItem)
          }
          return item
        })
        return {
          ...menu,
          ...(matchingOFSMenu && matchingOFSMenu),
          menuItems: items.sort(sortByRank),
        }
      })
      .sort(sortByRank)
  } catch (error) {
    consoleEvents.error('Failed to merge menu properties from OFS with initial userMenu: ', error)
    return defaultMenus
  }
}

interface MenuCategory {
  heading: string
  key: string
  menuItems: MenuItem[]
}

const filterMenuItemsByToggles = (menuItems: MenuItem[], toggles: object): MenuItem[] =>
  menuItems?.filter((menuItem) => toggles?.[menuItem.key])

const filterMenuByToggles =
  (menuToggles: MenuToggles) =>
  (acc: MenuCategory[], menuCategory: MenuCategory): MenuCategory[] => {
    const { key, menuItems = [] } = menuCategory

    const currentMenuToggles = menuToggles[key]

    if (currentMenuToggles) {
      return [
        ...acc,
        {
          ...menuCategory,
          menuItems: filterMenuItemsByToggles(menuItems, currentMenuToggles),
        },
      ]
    }

    return acc
  }

interface TriggerMenuTrackEventToggles {
  isDonateVoucherBaseLine?: boolean
}

const triggerMenuTrackEvent = (
  menuType: string,
  { isDonateVoucherBaseLine = false }: Partial<TriggerMenuTrackEventToggles> = {},
): void => {
  switch (menuType) {
    case MENU_ITEM_KEYS.pointsHistoryPage:
      if (isDonateVoucherBaseLine) {
        trackOptimizelyEvent(optimizelyConstants.CLICK_POINT_HISTORY_ENTRANCE)
      }

      trackEventAdapter({
        ...ACCOUNT_TRACKING_EVENTS.pointsHistoryEntry,
        event_label: 'from menu',
      })
      break
    case MENU_ITEM_KEYS.inviteFriendPage:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.inviteAFriendFromMenu)
      break
    case MENU_ITEM_KEYS.donateVoucher:
      trackOptimizelyEvent(optimizelyConstants.CLICK_DONATE_VOUCHER_MENU)
      break
    case MENU_ITEM_KEYS.purchases:
      trackEventAdapter({
        ...ACCOUNT_TRACKING_EVENTS.allPurchases,
        event_label: 'from menu',
      })
      break
    case MENU_ITEM_KEYS.paymentCredits:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.paymentSettingsFromMenu)
      break
    case MENU_ITEM_KEYS.mySettings:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.settingsFromMenu)
      break
    case MENU_ITEM_KEYS.reviewItems:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.ratingsFromMenu)
      break
    case MENU_ITEM_KEYS.digitalStylist:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.myStyleCornerFromMenu)
      break
    case MENU_ITEM_KEYS.clubInfoPage:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.aboutMembersshipFromMenu)
      break
    case MENU_ITEM_KEYS.customerService:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.contactUsFromMenu)
      break
    case MENU_ITEM_KEYS.logout:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.signOutFromMenu)
      break
    case MENU_ITEM_KEYS.hmxklarna:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.onKlarnaInvoicesMenuLinkClick)
      break
    default:
  }
}

const triggerUserMenuTrackEvent = (
  menuType: string,
  getElapsedTime: () => number,
  resetTimer: () => void,
  isNorthStarABtestEnabled: boolean,
): void => {
  trackOptimizelyEvent(optimizelyConstants.ACCOUNT_MENU_OPTIONS)
  switch (menuType) {
    case NEW_MENU_ITEM_KEYS.pointshistory || NEW_MENU_KEYS.mypoints:
      trackEventAdapter({
        ...ACCOUNT_TRACKING_EVENTS.pointsHistoryUserEntry,
        event_label: 'from bottom menu',
      })
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_POINTS_HISTORY_MENU)
      if (isNorthStarABtestEnabled) {
        trackOptimizelyEvent(optimizelyConstants.ACCOUNT_POINTS_HISTORY_BOTTOM_MENU)
      }
      break
    case NEW_MENU_ITEM_KEYS.helpus || NEW_MENU_KEYS.helpusimprove:
      trackEventAdapter({
        ...ACCOUNT_TRACKING_EVENTS.helpUsImproveEntry,
        event_label: 'from bottom menu',
      })

      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_HELP_US_IMPROVE_MENU)
      trackOptimizelyEvent(
        optimizelyConstants.ACCOUNT_TIME_TAKEN_TO_HELP_US_IMPROVE,
        undefined,
        {},
        { value: getElapsedTime() },
      )
      resetTimer()
      break
    case NEW_MENU_ITEM_KEYS.inviteafriend || NEW_MENU_KEYS.inviteafriend:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.inviteAFriendEntry)
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_INVITE_A_FRIEND_MENU)
      trackOptimizelyEvent(
        optimizelyConstants.ACCOUNT_TIME_TAKEN_TO_INVITE_A_FRIEND,
        undefined,
        {},
        { value: getElapsedTime() },
      )
      resetTimer()
      break
    case NEW_MENU_ITEM_KEYS.purchases || NEW_MENU_KEYS.myorders:
      trackEventAdapter({
        ...ACCOUNT_TRACKING_EVENTS.allPurchasesEntry,
        event_label: 'from bottom menu',
      })
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_ORDER_DETAILS_MENU)
      if (isNorthStarABtestEnabled) {
        trackOptimizelyEvent(optimizelyConstants.ACCOUNT_ORDER_BOTTOM_MENU)
      }
      break
    case NEW_MENU_ITEM_KEYS.payment || NEW_MENU_KEYS.paymentinformation:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.paymentSettingsEntry)
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_PAYMENTS_MENU)
      break
    case NEW_MENU_ITEM_KEYS.mysettings || NEW_MENU_KEYS.accountdetails:
      trackEventAdapter({
        ...ACCOUNT_TRACKING_EVENTS.settingsEntry,
        event_label: 'from bottom menu',
      })
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_SETTINGS_MENU)
      if (isNorthStarABtestEnabled) {
        trackOptimizelyEvent(optimizelyConstants.ACCOUNT_SETTINGS_BOTTOM_MENU)
      }
      break
    case NEW_MENU_ITEM_KEYS.review || NEW_MENU_KEYS.rateandreview:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.rateAndReviewEntry)
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_RATE_AND_REVIEW_MENU)
      break
    case NEW_MENU_ITEM_KEYS.klarna || NEW_MENU_KEYS.paymentsklarna:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.payLaterEntry)
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_PAYMENT_KLARNA_MENU)
      break
    case NEW_MENU_ITEM_KEYS.info || NEW_MENU_KEYS.membership:
      trackEventAdapter({
        ...ACCOUNT_TRACKING_EVENTS.aboutMembersshipEntry,
        event_label: 'from bottom menu',
      })
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_MEMBERSHIP_MENU)
      if (isNorthStarABtestEnabled) {
        trackOptimizelyEvent(optimizelyConstants.ACCOUNT_MEMBER_INFO_BOTTOM_MENU)
      }
      break
    case NEW_MENU_ITEM_KEYS.customerservice || NEW_MENU_KEYS.customerservice:
      trackEventAdapter({
        ...ACCOUNT_TRACKING_EVENTS.contactUsEntry,
        event_label: 'from bottom menu',
      })
      trackOptimizelyEvent(optimizelyConstants.ACCOUNT_CUSTOMER_SERVICE_MENU)
      if (isNorthStarABtestEnabled) {
        trackOptimizelyEvent(optimizelyConstants.ACCOUNT_CUSTOMER_SERVICE_BOTTOM_MENU)
      }
      break
    case NEW_MENU_ITEM_KEYS.signout:
      trackEventAdapter(ACCOUNT_TRACKING_EVENTS.signout)
      if (isNorthStarABtestEnabled) {
        trackOptimizelyEvent(optimizelyConstants.ACCOUNT_SIGN_OUT_BOTTOM_MENU)
      }
      break

    default:
  }
}
interface CreateMenuEvents {
  showDonateVoucher: boolean
  isDonateVoucherBaseLine: boolean
  translations: Record<string, any>
  dispatch: Dispatch<any>
}

const createMenuEvents =
  ({ showDonateVoucher, isDonateVoucherBaseLine, translations, dispatch }: CreateMenuEvents) =>
  (menu: UserMenu) => ({
    ...menu,
    heading: getLabelFromStorage(menu.heading),
    menuItems: [
      ...(showDonateVoucher && menu.key === MENU_SECTION_KEYS.account
        ? [
            {
              key: MENU_ITEM_KEYS.donateVoucher,
              title: translations[getLocale('lowerCase')].donateBonusVoucher,
              onClick: () => {
                triggerMenuTrackEvent(MENU_ITEM_KEYS.donateVoucher)
                dispatch(donateVoucherActions.openDonateVoucherModal())
              },
              isVisible: true,
            },
          ]
        : []),
      ...menu.menuItems.map((menuItem) => ({
        ...menuItem,
        title: getLabelFromStorage(menuItem.title),
        onClick: () => {
          triggerMenuTrackEvent(menuItem.key, { isDonateVoucherBaseLine })
        },
      })),
    ],
  })

const createUserMenuEvents = (
  menuItems,
  getElapsedTime,
  resetTimer,
  isNorthStarABtestEnabled,
): MenuItem[] => {
  return menuItems?.map((menuItem) => ({
    ...menuItem,
    title: getLabelFromStorage(menuItem.title),
    newTitle: getLabelFromStorage(menuItem.newTitle),
    subText: getLabelFromStorage(menuItem.subText),
    newTagText: getLabelFromStorage(menuItem.newTagText),
    onClick: () => {
      triggerUserMenuTrackEvent(menuItem.key, getElapsedTime, resetTimer, isNorthStarABtestEnabled)
    },
    ...(menuItem.badgeText
      ? {
          badgeText: getLabelFromStorage(menuItem.badgeText),
        }
      : {}),
  }))
}

export { mergeDefaultMenuWithOFS, filterMenuByToggles, createMenuEvents, createUserMenuEvents }
